import React from "react";

import { ResponsiveDialog } from "@replohq/lattice/blocks/modal/ResponsiveDialog";
import { Button } from "@replohq/lattice/components/button/Button";
import {
  CarouselContent,
  CarouselNextButton,
  CarouselPreviousButton,
  CarouselRoot,
  CarouselSlide,
  CarouselViewport,
} from "@replohq/lattice/components/carousel/Carousel";
import { ArrowLeft, ArrowRight, Sparkles } from "lucide-react";

export interface AnnouncementSlide {
  id: string;
  title: string;
  description: string;
  imageUrl?: string | null;
  videoUrl?: string | null;
  actionLabel: string;
}

function AnnouncementMedia({ slide }: { slide: AnnouncementSlide }) {
  const [videoFailed, setVideoFailed] = React.useState(false);
  const [imageFailed, setImageFailed] = React.useState(false);
  if (slide.videoUrl && !videoFailed) {
    return (
      <video
        src={slide.videoUrl}
        poster={slide.imageUrl ?? undefined}
        controls
        playsInline
        preload="metadata"
        className="aspect-video w-full rounded-lg bg-sheet object-contain"
        aria-label={`${slide.title} demo`}
        onError={() => setVideoFailed(true)}
        onKeyDown={(event) => {
          if (event.key === "ArrowLeft" || event.key === "ArrowRight") {
            event.stopPropagation();
          }
        }}
      />
    );
  }
  if (slide.imageUrl && !imageFailed) {
    return (
      <img
        src={slide.imageUrl}
        alt={`${slide.title} preview`}
        className="aspect-video w-full rounded-lg bg-sheet object-contain"
        onError={() => setImageFailed(true)}
      />
    );
  }
  return (
    <div
      className="flex aspect-video w-full items-center justify-center rounded-lg border border-border bg-sheet text-placeholder"
      aria-hidden
    >
      <Sparkles className="size-12" />
    </div>
  );
}

function VisibleAnnouncement<Slide extends AnnouncementSlide>({
  slide,
  onViewed,
}: {
  slide: Slide;
  onViewed: (slide: Slide) => void;
}) {
  const cleanup = React.useRef<(() => void) | null>(null);
  const observe = React.useCallback(
    (element: HTMLDivElement | null) => {
      cleanup.current?.();
      cleanup.current = null;
      if (!element) {
        return;
      }
      let visible = false;
      const report = () => {
        if (visible && document.visibilityState === "visible") {
          onViewed(slide);
        }
      };
      const observer = new IntersectionObserver((entries) => {
        visible = entries.some((entry) => entry.isIntersecting);
        report();
      });
      observer.observe(element);
      document.addEventListener("visibilitychange", report);
      cleanup.current = () => {
        observer.disconnect();
        document.removeEventListener("visibilitychange", report);
      };
    },
    [slide, onViewed],
  );

  return (
    <div ref={observe} className="flex flex-col gap-5">
      <AnnouncementMedia slide={slide} />
      <div className="flex flex-col gap-2">
        <h2 className="break-words text-2xl font-semibold leading-tight text-default">
          {slide.title}
        </h2>
        <p className="whitespace-pre-line break-words text-sm leading-relaxed text-muted">
          {slide.description}
        </p>
      </div>
    </div>
  );
}

export function AnnouncementCarousel<Slide extends AnnouncementSlide>({
  slides,
  open,
  onViewed,
  onDismiss,
  onEngage,
}: {
  slides: Slide[];
  open: boolean;
  onViewed: (slide: Slide) => void;
  onDismiss: (viewedSlides: Slide[]) => void;
  onEngage: (slide: Slide, viewedSlides: Slide[]) => void;
}) {
  const [index, setIndex] = React.useState(0);
  const viewedSlides = React.useRef(new Map<string, Slide>());
  const current = slides[index];
  const reportViewed = React.useCallback(
    (slide: Slide) => {
      if (!viewedSlides.current.has(slide.id)) {
        viewedSlides.current.set(slide.id, slide);
        onViewed(slide);
      }
    },
    [onViewed],
  );
  if (!current) {
    return null;
  }
  // A close gesture can arrive before the visibility observer's first callback.
  const settleViewed = () => {
    reportViewed(current);
    return [...viewedSlides.current.values()];
  };
  const dismiss = () => onDismiss(settleViewed());

  return (
    <ResponsiveDialog
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) {
          dismiss();
        }
      }}
      accessibleTitle="What's new in Replo"
      title="What's new"
      size="base"
      UNSAFE_className="grid-rows-[auto_minmax(0,1fr)_auto] overflow-hidden"
      footer={
        <div className="flex w-full flex-col gap-3">
          <div className="grid w-full grid-cols-1 gap-2 sm:grid-cols-2">
            <Button
              variant="secondary"
              className="min-h-11 w-full"
              onClick={dismiss}
            >
              Not now
            </Button>
            <Button
              className="h-auto min-h-11 w-full"
              onClick={() => onEngage(current, settleViewed())}
            >
              <span className="whitespace-normal break-words">
                {current.actionLabel}
              </span>
            </Button>
          </div>
        </div>
      }
    >
      <CarouselRoot
        index={index}
        onIndexChange={setIndex}
        aria-label="Product updates"
        className="min-h-0 min-w-0"
        UNSAFE_className="overflow-y-auto"
      >
        <CarouselViewport>
          <CarouselContent>
            {slides.map((slide, slideIndex) => (
              <CarouselSlide key={slide.id} className="basis-full">
                {slideIndex === index && (
                  <VisibleAnnouncement slide={slide} onViewed={reportViewed} />
                )}
              </CarouselSlide>
            ))}
          </CarouselContent>
        </CarouselViewport>
        {slides.length > 1 && (
          <div className="mt-5 flex items-center justify-between">
            <CarouselPreviousButton
              render={
                <Button
                  icon
                  variant="secondary"
                  className="min-h-11 min-w-11"
                />
              }
            >
              <ArrowLeft className="size-4" />
            </CarouselPreviousButton>
            <p aria-live="polite" className="text-sm text-muted">
              {index + 1} of {slides.length}
            </p>
            <CarouselNextButton
              render={
                <Button
                  icon
                  variant="secondary"
                  className="min-h-11 min-w-11"
                />
              }
            >
              <ArrowRight className="size-4" />
            </CarouselNextButton>
          </div>
        )}
      </CarouselRoot>
    </ResponsiveDialog>
  );
}
